import { useState } from 'react';
import {
  PauseIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/ui/Button';
import Modal, { ModalBody, ModalFooter } from '@/components/ui/Modal';
import type { TTSJobStatus } from '@/types/api';

export interface JobControlButtonsProps {
  jobId: string;
  status: TTSJobStatus;
  currentStep?: string;
  pauseRequested?: boolean;
  onPause: () => Promise<void>;
  onResume: () => Promise<void>;
  onStop: () => Promise<void>;
  onDelete?: () => Promise<void>;
}

export default function JobControlButtons({
  jobId,
  status,
  currentStep,
  pauseRequested,
  onPause,
  onResume,
  onStop,
  onDelete,
}: JobControlButtonsProps) {
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Determine what actions are available based on status
  const canPause = (status === 'processing' || status === 'running') && !pauseRequested;
  const canResume = status === 'paused';
  const canStop = status === 'processing' || status === 'running' || status === 'paused';
  const canDelete = status === 'stopped';

  const handlePause = async () => {
    setIsPausing(true);
    try {
      await onPause();
    } finally {
      setIsPausing(false);
    }
  };

  const handleResume = async () => {
    setIsResuming(true);
    try {
      await onResume();
    } finally {
      setIsResuming(false);
    }
  };

  const handleStop = async () => {
    setIsStopping(true);
    try {
      await onStop();
      setShowStopConfirm(false);
    } finally {
      setIsStopping(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {/* Pause Button */}
        {canPause && (
          <Button
            variant="secondary"
            onClick={handlePause}
            isLoading={isPausing}
            leftIcon={<PauseIcon className="w-4 h-4" />}
          >
            Pause at Next Checkpoint
          </Button>
        )}

        {/* Pause Requested Indicator */}
        {pauseRequested && (status === 'processing' || status === 'running') && (
          <Button
            variant="secondary"
            disabled
            leftIcon={<PauseIcon className="w-4 h-4" />}
          >
            Pause Requested...
          </Button>
        )}

        {/* Resume Button */}
        {canResume && (
          <Button
            variant="primary"
            onClick={handleResume}
            isLoading={isResuming}
            leftIcon={<PlayIcon className="w-4 h-4" />}
          >
            Resume Processing
          </Button>
        )}

        {/* Stop Button */}
        {canStop && (
          <Button
            variant="danger"
            onClick={() => setShowStopConfirm(true)}
            leftIcon={<StopIcon className="w-4 h-4" />}
          >
            Stop Job
          </Button>
        )}

        {/* Delete Button (only for stopped jobs) */}
        {canDelete && onDelete && (
          <Button
            variant="ghost"
            className="text-danger-500"
            onClick={() => setShowDeleteConfirm(true)}
            leftIcon={<TrashIcon className="w-4 h-4" />}
          >
            Delete Job
          </Button>
        )}
      </div>

      {/* Stop Confirmation Modal */}
      <Modal
        isOpen={showStopConfirm}
        onClose={() => setShowStopConfirm(false)}
        title="Stop Job?"
        description="This action cannot be undone."
        size="sm"
      >
        <ModalBody>
          <div className="space-y-4">
            <p className="text-slate-600">
              Are you sure you want to stop this job? The job will be permanently
              cancelled and cannot be resumed.
            </p>
            <div className="p-3 bg-warning-50 rounded-lg">
              <p className="text-sm text-warning-700">
                <strong>Note:</strong> Any partial outputs (processed pages, text
                extractions) will be preserved and can be downloaded.
              </p>
            </div>
            <div className="text-sm text-slate-500">
              <p>
                <strong>Job ID:</strong> {jobId}
              </p>
              {currentStep && (
                <p>
                  <strong>Current Step:</strong> {currentStep.replace(/_/g, ' ')}
                </p>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowStopConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleStop}
            isLoading={isStopping}
            leftIcon={<StopIcon className="w-4 h-4" />}
          >
            Stop Job
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Job?"
        description="This will permanently delete the job and all its files."
        size="sm"
      >
        <ModalBody>
          <div className="space-y-4">
            <p className="text-slate-600">
              Are you sure you want to delete this stopped job? All associated
              files and data will be permanently removed.
            </p>
            <div className="text-sm text-slate-500">
              <p>
                <strong>Job ID:</strong> {jobId}
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            isLoading={isDeleting}
            leftIcon={<TrashIcon className="w-4 h-4" />}
          >
            Delete Job
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
